import { APICONFIG } from '@config/api.config';
import { APIService } from '@services/api/common-services';

/** Mirrors HMpower IDfy KYC API response shapes (BST/api.js). */
export interface IdfyKycStatusPayload {
	document_img?: string;
	face_img?: string;
	status?: string;
	message?: string;
	attempt?: number;
}

export const IDFY_MAX_KYC_ATTEMPTS = 3;

export const IDFY_MAX_ATTEMPTS_DEFAULT_MESSAGE =
	'You have reached your maximum attempts limit, Please contact to call center';

export const getIdfyKycAttemptCount = (p: IdfyKycStatusPayload | null | undefined): number => {
	const n = p?.attempt;
	if (typeof n === 'number' && Number.isFinite(n)) {
		return n;
	}
	if (typeof n === 'string' && n.trim() !== '') {
		const parsed = Number(n);
		return Number.isFinite(parsed) ? parsed : 0;
	}
	return 0;
};

/** True when check-status `attempt` is at or above the allowed maximum (no further uploads). */
export const isIdfyKycMaxAttemptsReached = (p: IdfyKycStatusPayload | null | undefined): boolean => {
	return getIdfyKycAttemptCount(p) >= IDFY_MAX_KYC_ATTEMPTS;
};

export const getIdfyMaxAttemptsMessage = (p: IdfyKycStatusPayload | null | undefined): string => {
	const msg = p?.message?.trim();
	return msg || IDFY_MAX_ATTEMPTS_DEFAULT_MESSAGE;
};

const IDFY_REJECTED_DEFAULT_MESSAGE = 'Your document KYC was rejected. Please try again.';

/** Footer copy for check-status: rejection remark plus max-attempts line when both apply. */
export const buildIdfyKycStatusFooterMessage = (p: IdfyKycStatusPayload | null | undefined): string => {
	if (!p) {
		return '';
	}
	const maxReached = isIdfyKycMaxAttemptsReached(p);
	const rejected = isIdfyKycStatusPayloadRejected(p);
	const lines: string[] = [];
	const apiMsg = p.message?.trim();

	if (rejected) {
		const rejectionLine =
			apiMsg && (!maxReached || apiMsg !== IDFY_MAX_ATTEMPTS_DEFAULT_MESSAGE) ? apiMsg : IDFY_REJECTED_DEFAULT_MESSAGE;
		lines.push(rejectionLine);
	}

	if (maxReached) {
		lines.push(IDFY_MAX_ATTEMPTS_DEFAULT_MESSAGE);
	}

	if (!lines.length && apiMsg) {
		lines.push(apiMsg);
	}

	return lines.join('\n');
};

export interface IdfyVerifyDocumentResponse {
	status?: boolean | string;
	message?: string;
}

export interface IdfyVerifySelfieResponse {
	status?: string | boolean;
	message?: string;
}

const buildFormData = (imageUrlOrPath: string, membershipNo: string): FormData => {
	const formData = new FormData();
	formData.append('img', imageUrlOrPath);
	formData.append('membership_no', membershipNo);
	return formData;
};

/** GET current verification snapshot for a membership (visible in Network tab). */
export const getIdfyKycStatus = async (membershipNo: string): Promise<IdfyKycStatusPayload> => {
	const url = `${APICONFIG.IDFY_CHECK_STATUS}?membership_no=${encodeURIComponent(membershipNo)}`;
	return await APIService.getData<IdfyKycStatusPayload>(url);
};

export const isIdfyDocumentCheckSuccess = (res: IdfyVerifyDocumentResponse): boolean => {
	const s = res?.status;
	if (s === true) return true;
	if (typeof s === 'string') {
		const t = s.trim().toLowerCase();
		return t === 'true' || t === 'success' || t === 'approved' || t === 'pass' || t === 'passed';
	}
	return Boolean(s);
};

/** Face compare / live selfie check — treat common success tokens from IDfy-style APIs as match. */
/** True when check-status payload indicates KYC is already complete on the server. */
export const isIdfyKycStatusPayloadApproved = (p: IdfyKycStatusPayload | null | undefined): boolean => {
	const s = p?.status?.trim().toLowerCase();
	if (!s) {
		return false;
	}
	return (
		s === 'approved' ||
		s === 'success' ||
		s === 'pass' ||
		s === 'passed' ||
		s === 'completed' ||
		s === 'complete' ||
		s === 'kyc approved' ||
		s === 'verified' ||
		s === 'true'
	);
};

/** True when check-status payload indicates document or KYC was rejected (retry required). */
export const isIdfyKycStatusPayloadRejected = (p: IdfyKycStatusPayload | null | undefined): boolean => {
	const s = p?.status?.trim().toLowerCase();
	if (!s) {
		return false;
	}
	return s === 'rejected' || s === 'reject' || s === 'failed' || s === 'failure' || s === 'declined';
};

export const isIdfyFaceCompareSuccess = (res: IdfyVerifySelfieResponse): boolean => {
	const s = res?.status;
	if (typeof s === 'boolean' && s) {
		return true;
	}
	if (typeof s === 'string') {
		const t = s.trim().toLowerCase();
		return (
			t === 'true' ||
			t === 'success' ||
			t === 'approved' ||
			t === 'pass' ||
			t === 'passed' ||
			t === 'match' ||
			t === 'matched' ||
			t === 'kyc approved'
		);
	}
	return Boolean(s);
};

export const verifyIdfyKycDocument = async (
	documentImageUrl: string,
	membershipNo: string
): Promise<IdfyVerifyDocumentResponse> => {
	return await APIService.postFormData<IdfyVerifyDocumentResponse>(
		APICONFIG.IDFY_CHECK_DOCUMENT,
		buildFormData(documentImageUrl, membershipNo)
	);
};

export const verifyIdfyKycSelfie = async (
	faceImageUrl: string,
	membershipNo: string
): Promise<IdfyVerifySelfieResponse> => {
	return await APIService.postFormData<IdfyVerifySelfieResponse>(
		APICONFIG.IDFY_FACE_COMPARE_LIVE,
		buildFormData(faceImageUrl, membershipNo)
	);
};

/**
 * Turn IDfy / API error blobs (HTML, MESSAGE: … STATUS: …, or plain text) into a single readable sentence for the UI.
 */
export const formatIdfyErrorMessage = (raw: string): string => {
	const t = raw?.trim();
	if (!t) {
		return '';
	}
	let s = t
		.replace(/<br\s*\/?>/gi, ' ')
		.replace(/\r?\n/g, ' ')
		.replace(/<[^>]+>/g, ' ');
	const msgMatch = s.match(/MESSAGE:\s*(.+?)(?:\s+STATUS:|$)/i);
	if (msgMatch?.[1]) {
		return msgMatch[1].replace(/\s+/g, ' ').trim();
	}
	s = s.replace(/\s+/g, ' ').trim();
	return s.replace(/^Error:\s*/i, '').trim() || t;
};

/** @deprecated Use formatIdfyErrorMessage */
export const formatIdfySelfieErrorMessage = formatIdfyErrorMessage;
